import React, { useRef } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { MenuItem } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/MenuItem';

import { getAgentIcon } from '../../../utils/agent-display/index.tsx';
import { useInViewport } from '../../../utils/use-is-in-viewport/index.ts';

const styles = cssMap({
	container: {
		minHeight: '32px',
	},
});

interface AgentMenuItemProps {
	agent: Agent;
	isSelected: boolean;
	onSelect: (agent: Agent) => void;
	onFavoriteToggle?: (agent: Agent | undefined) => void;
	renderImmediately: boolean;
	comboBoxOptionProps?: React.HTMLAttributes<HTMLElement>;
	isFocused?: boolean;
}

export const AgentMenuItem = ({
	agent,
	isSelected,
	onSelect,
	onFavoriteToggle,
	renderImmediately,
	comboBoxOptionProps,
	isFocused,
}: AgentMenuItemProps): React.JSX.Element => {
	const containerRef = useRef<HTMLElement>(null);
	const { hasBeenInViewport } = useInViewport({ ref: containerRef });

	// Always render the first N items immediately, use viewport check for items beyond that
	const shouldRender = renderImmediately || hasBeenInViewport;

	return (
		<Box ref={containerRef} xcss={styles.container} {...comboBoxOptionProps}>
			{shouldRender && (
				<MenuItem
					label={agent.name}
					icon={getAgentIcon(agent)}
					onClick={() => onSelect(agent)}
					isActive={isSelected}
					isFocused={isFocused}
					isFavorite={agent.favourite}
					isVerified={agent.is_verified}
					onFavoriteClick={onFavoriteToggle ? () => onFavoriteToggle(agent) : undefined}
				/>
			)}
		</Box>
	);
};
